import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { travels } from "@/lib/endpoints";
import { useApplicant } from "@/hooks/useApplicant";
import { useNestedList } from "@/hooks/useNestedList";
import { Field, Select, TextInput } from "@/components/Field";
import { FieldHelpButton } from "@/components/FieldHelpButton";
import type { CreateTravelRequest, TravelResponse } from "@/lib/api-types";
import { getFieldErrors, getFormError } from "@/lib/validation-errors";

/**
 * Step 4 — Travel outside the U.S. (Part 9 of N-400).
 * Each trip in the past 5 years.
 */
export function StepTravel() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicant, isLoading: applicantLoading } = useApplicant(id);
  const list = useNestedList<TravelResponse, CreateTravelRequest>(
    "travels",
    travels,
    id,
    applicant?.id,
  );

  const [editing, setEditing] = useState<TravelResponse | "new" | null>(null);

  if (applicantLoading || list.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Trips outside the U.S.</h1>
        <p className="text-sm text-slate-500 mt-1">
          Every trip in the past 5 years — even short ones to Canada or Mexico.
          Skip if you haven&apos;t left the country.
        </p>
      </header>

      <ul className="space-y-3">
        {list.items.map((t) => (
          <li
            key={t.id}
            className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between"
          >
            <div className="text-sm">
              <div className="font-medium">{(t.countries_visited ?? []).join(", ") || "Trip"}</div>
              <div className="text-slate-500">{t.purpose}</div>
              <div className="text-xs text-slate-400 mt-1">
                {t.departure_date} → {t.return_date}
                {t.duration_days != null ? ` · ${t.duration_days} days` : ""}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setEditing(t)}
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm("Delete this trip?")) list.remove.mutate(t.id);
                }}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {!editing && (
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          + Add trip
        </button>
      )}

      {editing && (
        <TravelForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSubmit={async (req) => {
            if (editing === "new") await list.create.mutateAsync(req);
            else await list.update.mutateAsync({ id: editing.id, req });
            setEditing(null);
          }}
          saving={list.create.isPending || list.update.isPending}
          error={editing === "new" ? list.create.error : list.update.error}
        />
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link to={`/cases/${id}/wizard/employment`} className="text-sm text-slate-600 hover:text-slate-900">
          ← Employment
        </Link>
        <button
          type="button"
          onClick={() => navigate(`/cases/${id}/wizard/marriage`)}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800"
        >
          Save & continue
        </button>
      </div>
    </div>
  );
}

function TravelForm({
  initial,
  onCancel,
  onSubmit,
  saving,
  error,
}: {
  initial: TravelResponse | null;
  onCancel: () => void;
  onSubmit: (req: CreateTravelRequest) => Promise<void>;
  saving: boolean;
  error?: unknown;
}) {
  const [countries, setCountries] = useState((initial?.countries_visited ?? []).join(", "));
  const [departure, setDeparture] = useState(initial?.departure_date ?? "");
  const [ret, setRet] = useState(initial?.return_date ?? "");
  const [purpose, setPurpose] = useState(initial?.purpose ?? "");

  const days = computeDays(departure, ret);
  const fieldErrors = getFieldErrors(error);
  const formError = getFormError(error);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit({
          countries_visited: countries
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean),
          departure_date: departure || undefined,
          return_date: ret || undefined,
          purpose: purpose || undefined,
          duration_days: days ?? undefined,
        });
      }}
      className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4"
    >
      <h3 className="font-semibold">{initial ? "Edit trip" : "Add trip"}</h3>
      <Field
        label="Countries visited"
        required
        hint="Comma-separated, e.g. Mexico, Canada, Germany"
        error={fieldErrors?.["countries_visited"]?.message}
      >
        <TextInput value={countries} onChange={(e) => setCountries(e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label={<>Departure date <FieldHelpButton question="What trips outside the U.S. do I need to list on the N-400?" /></>}
          required
          error={fieldErrors?.["departure_date"]?.message}
        >
          <TextInput type="date" value={departure} onChange={(e) => setDeparture(e.target.value)} />
        </Field>
        <Field
          label="Return date"
          required
          error={fieldErrors?.["return_date"]?.message}
        >
          <TextInput type="date" value={ret} onChange={(e) => setRet(e.target.value)} />
        </Field>
      </div>
      <Field
        label="Purpose of trip"
        hint="Pick the closest category. USCIS uses these to flag continuous-residence breaks."
        error={fieldErrors?.["purpose"]?.message}
      >
        <Select value={purpose} onChange={(e) => setPurpose(e.target.value)}>
          <option value="">Select…</option>
          <option value="tourism">Tourism / vacation</option>
          <option value="business">Business / work</option>
          <option value="family">Family visit</option>
          <option value="other">Other</option>
        </Select>
      </Field>
      {days != null && (
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500">Calculated duration: {days} day{days === 1 ? "" : "s"}</p>
          {days >= 180 && (
            <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
              <span className="shrink-0 mt-0.5">⚠️</span>
              <span>
                Trips of 180+ days may affect continuous residence.{" "}
                <FieldHelpButton question="My trip was over 180 days. Is that a problem for my N-400 application?" />
              </span>
            </div>
          )}
        </div>
      )}
      {formError && (
        <p className="text-sm text-red-600">Could not save: {formError}</p>
      )}
      {fieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {saving ? "Saving…" : initial ? "Save changes" : "Add"}
        </button>
      </div>
    </form>
  );
}

function computeDays(start: string, end: string): number | null {
  if (!start || !end) return null;
  const d1 = new Date(start);
  const d2 = new Date(end);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return null;
  return Math.max(0, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)));
}
